import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ActiveTimer, Mode } from '@timato/shared';
import { useActiveTimer, useSettings } from '@/data/hooks';
import { saveRow } from '@/data/repo';
import { isThisDevice, timerDeviceId } from '@/auth/thisDevice';
import { playChime, primeAudio } from '@/lib/chime';
import { ensureNotificationPermission, notifyInBackground } from '@/lib/notify';
import { formatClock } from '@/lib/time';
import { clockNow } from '@/lib/clock';
import { toast } from 'sonner';
import { db } from '@/db/db';
import { formatDuration } from '@/lib/time';
import { completeCountdown, finishTrack, skipCountdown } from './completion';
import { useCompletionStore } from './completionStore';
import { applyOutcome, claimToStart, transitionCtx } from './transitions';
import { goalChangeSwitches, renameInstead, requestFromGoal, switchTrack } from './startTimer';
import type { Goal } from './goal';
import { useCore } from '@/data/core';
import { useIdleTracker } from './useIdleTracker';
import { useWakeLock } from './useWakeLock';
import * as engine from './engine';
import { useNow } from './useNow';

/** One ticking clock for a row: re-renders when the whole second shown would change, not every frame. */
function useTimerClock(timer: ActiveTimer) {
  const derive = useCallback((n: Date) => Math.floor(engine.remainingSec(timer, n) ?? engine.elapsedSec(timer, n)), [timer]);
  return useNow(timer.status === 'running', derive);
}

/**
 * The side of the timer that must run whatever screen is open: completing a countdown when it ends, the chime and
 * the notification, the tab title, the wake lock, the idle tracker and the long-run reminder. Mounted once in the
 * shell. The Timer screen only renders and offers actions; leaving it must never stop the clock.
 */
export function useTimerHeartbeat() {
  const timer = useActiveTimer();
  const settings = useSettings();
  const running = timer.status === 'running';
  const setCompletion = useCompletionStore((s) => s.set);
  useIdleTracker(running && timer.mode === 'track');
  useWakeLock(running);
  const now = useTimerClock(timer);
  const elapsed = engine.elapsedSec(timer, now);

  // Completion: fires once per run, also right after a reload or wake if the countdown ended meanwhile.
  const completedRun = useRef<string | null>(null);
  useEffect(() => {
    if (!engine.isComplete(timer, now) || completedRun.current === timer.runId) return;
    completedRun.current = timer.runId;
    void (async () => {
      const c = await transitionCtx(timer, settings);
      const outcome = completeCountdown(timer, c);
      await applyOutcome(outcome);
      if (outcome.session) setCompletion({ session: outcome.session, timer, kind: engine.isBreak(timer.mode) ? 'break' : 'focus' });
      if (isThisDevice(timer.startedOnDeviceId)) {
        const label = engine.isBreak(timer.mode) ? 'Break over' : 'Focus complete';
        if (settings.sound) playChime();
        if (settings.notifications) notifyInBackground(label, engine.isBreak(timer.mode) ? 'Ready for the next focus session.' : timer.goalText || 'Time for a break.');
      }
    })();
  }, [timer, now, settings, setCompletion]);

  // Tab title carries the clock while a run is live (Settings › Timer › "Show seconds in tab title").
  useEffect(() => {
    if (timer.status === 'idle') { document.title = 'Timato'; return; }
    const clock = formatClock(engine.remainingSec(timer, now) ?? elapsed);
    const shown = settings.tabTitleSeconds ? clock : clock.replace(/:\d\d$/, '');
    document.title = `${shown} · ${engine.MODE_LABEL[timer.mode]} - Timato`;
  }, [timer, now, elapsed, settings.tabTitleSeconds]);

  // Long-run reminder (Track only): first nudge after N minutes, then every 60m (8a, 8k).
  const remindedAt = useRef<number>(-1);
  useEffect(() => {
    if (!running || timer.mode !== 'track' || !settings.longRunReminderMin) { remindedAt.current = -1; return; }
    const first = settings.longRunReminderMin * 60;
    if (elapsed < first) return;
    const key = Math.floor((elapsed - first) / 3600);
    if (key === remindedAt.current) return;
    remindedAt.current = key;
    const body = `You've been on ${timer.goalText || 'this'} for ${formatDuration(elapsed)} without a break.`;
    const finish = async () => { const outcome = finishTrack(timer, await transitionCtx(timer, settings)); await applyOutcome(outcome); if (outcome.session) setCompletion({ session: outcome.session, timer, kind: 'track' }); };
    toast('Still tracking?', { description: body, duration: 30_000, action: { label: 'Finish', onClick: () => void finish() } });
    if (settings.notifications) notifyInBackground('Still tracking?', body);
  }, [running, timer, settings, elapsed, setCompletion]);
}

/**
 * A mode tab picked while a run is live (TW-33): the screen shows that mode ready to start while the run carries on.
 * It belongs to the run it was picked over (`runId`), so it lapses by itself once that run ends. Start, from the
 * button or Space, calls `onStart` instead, which asks before the run is parked or stopped.
 */
export type ModePreview = { mode: Mode; runId: string | null; onStart: () => void };

/** Everything the Timer screen needs: the live row, derived display values, and the transitions as actions. */
export function useTimer(preview: ModePreview | null = null) {
  const live = useActiveTimer();
  const previewing = !!preview && live.status !== 'idle' && preview.runId === live.runId && preview.mode !== live.mode;
  const previewMode = previewing ? preview.mode : null;
  // What the screen shows: the live row, or the previewed mode idle on top of it. The clock keeps the live run's pace.
  const timer = useMemo(() => (previewMode ? engine.setMode(live, previewMode) : live), [live, previewMode]);
  const onPreviewStart = preview?.onStart;
  const settings = useSettings();
  const setCompletion = useCompletionStore((s) => s.set);
  const now = useTimerClock(live);

  const elapsed = engine.elapsedSec(timer, now);
  const remaining = engine.remainingSec(timer, now);
  const displaySec = timer.status === 'idle' ? (engine.durationFor(timer.mode, settings) ?? 0) : remaining ?? elapsed;
  const clock = formatClock(displaySec);

  const ctx = useCallback((t: ActiveTimer) => transitionCtx(t, settings), [settings]);
  const apply = applyOutcome;

  // Every write is to the live row: in a preview (TW-33) `timer` is only what the screen shows.
  const actions = useMemo(() => ({
    async start(goal?: Pick<ActiveTimer, 'taskId' | 'projectId' | 'goalText'>) {
      if (previewMode) { onPreviewStart?.(); return; }
      primeAudio();
      setCompletion(null);
      if (settings.notifications) void ensureNotificationPermission();
      const taskId = goal?.taskId ?? live.taskId;
      if (!await claimToStart(taskId ? await db.tasks.get(taskId) : null, live.mode)) return;
      await saveRow('activeTimer', engine.start(live, { now: clockNow(), settings, deviceId: timerDeviceId() }, goal));
    },
    toggle() {
      if (previewMode) { onPreviewStart?.(); return; }
      if (live.status === 'running') return saveRow('activeTimer', engine.pause(live, clockNow()));
      if (live.status === 'paused') return saveRow('activeTimer', engine.resume(live, clockNow()));
      return this.start();
    },
    // In a preview these would act on the run beneath it; the screen offers none of them there (TW-33).
    reset: () => (previewMode ? undefined : saveRow('activeTimer', engine.reset(live))),
    setMode: (mode: Mode) => saveRow('activeTimer', engine.setMode(live, mode)),
    async skip() { if (!previewMode) await apply(skipCountdown(live, await ctx(live))); },
    async finish() {
      if (previewMode) return;
      const outcome = finishTrack(live, await ctx(live));
      await apply(outcome);
      if (outcome.session) setCompletion({ session: outcome.session, timer: live, kind: 'track' });
    },
    /** Skip whatever is running (usually the auto-started break) and begin a focus session now. */
    async startFocusNow() {
      primeAudio(); setCompletion(null);
      const fresh = (await db.activeTimer.get('active')) ?? live;
      await saveRow('activeTimer', engine.start(engine.setMode(fresh, 'focus'), { now: clockNow(), settings, deviceId: timerDeviceId() }));
    },
    /**
     * The goal field's pick. On a Track run with time on it, a different goal switches (TW-31): the run so far is
     * parked under the old goal (TW-32) and Track goes on with the new one, with a toast to rename instead.
     * Otherwise it renames.
     */
    async chooseGoal(goal: Goal) {
      const current = (await db.activeTimer.get('active')) ?? live;
      if (!goalChangeSwitches(current, goal, clockNow())) { await saveRow('activeTimer', engine.setGoal(current, goal)); return; }
      setCompletion(null);
      const done = await switchTrack(await requestFromGoal(goal));
      if (!done?.ended) return;
      const name = (g: Goal) => g.goalText.trim() || useCore.getState().projects.find((p) => p.id === g.projectId)?.name || 'no goal';
      toast(`Parked “${name(current)}” at ${formatDuration(done.ended.durationSec, { zero: '<1m' })}`, {
        description: `Now tracking “${name(goal)}”. Switch back to carry on.`, duration: 10_000,
        action: { label: 'Rename instead', onClick: () => void renameInstead(done, goal) },
      });
    },
    /** This session's labels only (TW-6): the task keeps its own, and the next session starts from the goal's again. */
    setLabels: (labelIds: string[]) => saveRow('activeTimer', { ...live, labelIds }),
  }), [live, settings, ctx, apply, setCompletion, previewMode, onPreviewStart]);

  return {
    timer, live, previewing, settings, now, clock, elapsed, remaining,
    progress: engine.progress(timer, now),
    status: engine.statusLabel(timer, now),
    cycle: engine.cycleLabel(timer, settings),
    isCountdown: engine.isCountdown(timer.mode),
    actions,
  };
}
export type TimerApi = ReturnType<typeof useTimer>;
